// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { Check, MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/common/EmptyState";
import StatusBadge from "@/components/common/StatusBadge";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/utils/common.utils";
import { DEAL_COMMUNICATION_STATUS } from "@/constant/deals.constant";
import { formatMessageDate } from "@/utils/deals-view.utils";
import { AskStatus } from "@/enum/common.enum";

function MessageCard({ ask, open, reply, onToggle, onReplyChange, onReply, onResolve, onConvertToTask }) {
  const status = DEAL_COMMUNICATION_STATUS[ask.status] || DEAL_COMMUNICATION_STATUS.open;
  const resolved = ask.status === AskStatus.CLOSED;
  return (
    <li className={cn("rounded-xl border bg-white px-4 py-5 sm:px-5", ask.status === AskStatus.OPEN ? "border-dash-amber/35" : "border-dash-border")} data-testid={`internal-ask-${ask.id}`}>
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {ask.status === AskStatus.OPEN && <span className="size-2 shrink-0 rounded-full bg-dash-amber" aria-hidden="true" />}
            <h3 className="truncate text-sm font-semibold text-dash-ink">{ask.subject}</h3>
          </div>
          <p className={cn("mt-1 text-xs text-dash-muted", ask.status === "open" && "ml-4")}>
            {ask.opened_by === "agent"
              ? `You → ${ask.client_name || "Client"}`
              : `From ${ask.client_name || "Client"}`} · {formatMessageDate(ask.created_at)}
          </p>
        </div>
        <StatusBadge tone={status.tone} className="shrink-0 px-2.5 py-1 text-[10px] font-medium sm:self-start">{status.label}</StatusBadge>
      </div>

      <p className={cn("mt-3 text-sm leading-6 text-dash-faint", open ? "whitespace-pre-wrap" : "line-clamp-2")}>{ask.body}</p>

      <div className="mt-3 flex flex-wrap items-center gap-5">
        <Button variant="seamless" type="button" onClick={onToggle} className="text-xs font-medium text-dash-ink hover:text-primary" data-testid={`internal-ask-toggle-${ask.id}`}>Reply</Button>
        <Button variant="seamless" type="button" onClick={() => onConvertToTask?.(ask)} aria-disabled={!onConvertToTask} className="text-xs text-dash-muted hover:text-dash-ink">Convert to task</Button>
        {!resolved && <Button variant="seamless" type="button" onClick={() => onResolve(ask)} className="text-xs text-dash-muted hover:text-dash-ink" data-testid={`internal-ask-close-${ask.id}`}>Mark resolved</Button>}
      </div>

      {open && (
        <div className="mt-5 border-t border-dash-border pt-4">
          {ask.replies?.length > 0 && (
            <div className="mb-4 flex flex-col gap-3">
              {ask.replies.map((item) => (
                <div key={item.id} className="rounded-xl bg-dash-searchBg p-4">
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="font-medium text-dash-ink">{item.by_user_name || "Team member"}</span>
                    <span className="text-dash-faint">{formatMessageDate(item.created_at)}</span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-dash-muted">{item.body}</p>
                </div>
              ))}
            </div>
          )}

          {!resolved ? (
            <div className="flex flex-col gap-3">
              <Textarea value={reply} onChange={(event) => onReplyChange(event.target.value)} placeholder="Reply to your client…" rows={3} className="min-h-24 rounded-xl border-dash-border bg-white" data-testid={`internal-ask-reply-${ask.id}`} />
              <div className="flex justify-end">
                <Button type="button" variant="dashboardPrimary" size="sm" onClick={() => onReply(ask)} disabled={!reply.trim()} className="w-full sm:w-auto" data-testid={`internal-ask-send-${ask.id}`}>
                  <Send data-icon="inline-start" /> Send reply
                </Button>
              </div>
            </div>
          ) : (
            <p className="flex items-center gap-2 text-xs text-dash-muted"><Check className="size-3.5 text-dash-greenDeep" /> Thread resolved</p>
          )}
        </div>
      )}
    </li>
  );
}

export default function CommunicationTab({ asks, openAskId, onToggleAsk, askReply, onAskReplyChange, onCloseAsk, onSendAskReply, onConvertToTask, onSendUpdate }) {
  return (
    <section data-testid="voice-tab">
      <div className="mb-5 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <h2 className="text-base font-semibold text-dash-ink">Messages &amp; requests</h2>
        <Button type="button" variant="dashboardPrimary" size="lg" onClick={onSendUpdate} className="w-full sm:min-w-36 sm:w-auto" data-testid="communication-send-update">
          Send update
        </Button>
      </div>

      {!asks.length ? (
        <EmptyState compact icon={MessageCircle} title="No messages yet" description="Client questions, requests, and deal updates will appear here." />
      ) : (
        <ul className="flex flex-col gap-4">
          {asks.map((ask) => (
            <MessageCard
              key={ask.id}
              ask={ask}
              open={openAskId === ask.id}
              reply={openAskId === ask.id ? askReply : ""}
              onToggle={() => onToggleAsk(openAskId === ask.id ? null : ask.id)}
              onReplyChange={onAskReplyChange}
              onReply={onSendAskReply}
              onResolve={onCloseAsk}
              onConvertToTask={onConvertToTask}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
